import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ArrowLeft,
  Package,
  User,
  Calendar,
  CheckCircle2,
  XCircle,
  AlertCircle,
  AlertTriangle,
  Edit2,
  ChevronDown,
  ChevronUp,
  Plus,
  Trash2,
  Copy,
  ClipboardCheck,
  ChevronLeft,
  ChevronRight,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { supabase } from '@/lib/supabase';
import { writeAuditLog } from '@/lib/audit';
import { useListNavigation } from '@/lib/useListNavigation';
import { formatBuildSku } from '@/lib/utils';
import { toast } from 'sonner';
import { HelpTooltip } from '@/components/HelpTooltip';
import { fifoReallocate } from '@/lib/fifoReallocate';
import { recomputeAllocations } from '@/lib/reservationAllocation';
import { isManagerOrAbove, isSuperAdmin as isSuperAdminRole } from '@/lib/roles';
import { useCurrentUser } from '@/lib/useCurrentUser';
import {
  classifyStockCoverage,
  STOCK_COVERAGE_CLASS,
  STOCK_COVERAGE_LABEL,
  STOCK_COVERAGE_SWATCH_BORDER,
  STOCK_COVERAGE_ORDER,
} from '@/lib/stockCoverage';
import {
  deriveReservationStatus,
  resolveFulfilledUnits,
  sumAssemblyOrderUnits,
  isTerminalStatus,
} from '@/lib/reservationStatus';
import { 
  getReservationStatusColor, 
  getReservationStatusLabel,
  getComponentTypeLabel,
  getComponentSortRank,
  calculateFulfillmentPercentage,
  type ReservationWithDetails
} from '@/types/database';

// ── Build search input ────────────────────────────────────────────────────────
function BuildSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (sku: string, buildId: string | null) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 320 });

  useEffect(() => { setSearch(value); }, [value]);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('builds')
        .select('id, sku, description, status')
        .or(`sku.ilike.%${search}%,description.ilike.%${search}%`)
        .limit(12);
      setResults(data || []);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 320) });
    }
    setOpen(true);
  };

  return (
    <div>
      <input
        ref={inputRef}
        className="w-full border rounded px-3 py-2 text-sm bg-background"
        placeholder="Search build SKU or description…"
        value={search}
        onChange={e => { setSearch(formatBuildSku(e.target.value)); openDropdown(); }}
        onFocus={() => { if (search.length >= 2) openDropdown(); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto"
        >
          {results.map(b => (
            <button
              key={b.id}
              type="button"
              className="w-full text-left px-3 py-2 text-sm hover:bg-muted/60 border-b border-gray-100 last:border-0"
              onMouseDown={() => {
                setSearch(b.sku);
                setOpen(false);
                onChange(b.sku, b.id);
              }}
            >
              <span className="font-mono font-semibold text-primary">{b.sku}</span>
              <span className="ml-2 text-muted-foreground text-xs">{b.description}</span>
              {b.status && b.status !== 'Active' && (
                <span className="ml-1 text-xs text-orange-600">({b.status})</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Component search with auto-fill ──────────────────────────────────────────
function ComponentSearchInput({
  value,
  onChange,
}: {
  value: string;
  onChange: (sku: string, type: string, cost: number, desc: string) => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [search, setSearch] = useState(value);
  const [results, setResults] = useState<any[]>([]);
  const [open, setOpen] = useState(false);
  const [dropdownPos, setDropdownPos] = useState<{ top: number; left: number; width: number }>({ top: 0, left: 0, width: 380 });

  useEffect(() => { setSearch(value); }, [value]);

  useEffect(() => {
    if (search.length < 2) { setResults([]); return; }
    const timer = setTimeout(async () => {
      const { data } = await supabase
        .from('components')
        .select('part_number, description, category, current_price, status')
        .or(`part_number.ilike.%${search}%,description.ilike.%${search}%`)
        .limit(12);
      setResults(data || []);
    }, 300);
    return () => clearTimeout(timer);
  }, [search]);

  const openDropdown = () => {
    if (inputRef.current) {
      const rect = inputRef.current.getBoundingClientRect();
      setDropdownPos({ top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 380) });
    }
    setOpen(true);
  };

  return (
    <div>
      <input
        ref={inputRef}
        className="w-full border rounded px-2 py-1 text-xs font-mono bg-background"
        placeholder="Search part no. or description…"
        value={search}
        onChange={e => { setSearch(e.target.value); openDropdown(); }}
        onFocus={() => { if (search.length >= 2) openDropdown(); }}
        onBlur={() => setTimeout(() => setOpen(false), 150)}
      />
      {open && results.length > 0 && (
        <div
          style={{ position: 'fixed', top: dropdownPos.top, left: dropdownPos.left, width: dropdownPos.width, zIndex: 9999 }}
          className="bg-white border border-gray-200 rounded-md shadow-lg max-h-52 overflow-y-auto"
        >
          {results.map(c => (
            <button
              key={c.part_number}
              type="button"
              className="w-full text-left px-3 py-2 text-xs hover:bg-muted/60 border-b border-gray-100 last:border-0 flex gap-3 items-start"
              onMouseDown={() => {
                setSearch(c.part_number);
                setOpen(false);
                onChange(c.part_number, c.category || '', c.current_price || 0, c.description || '');
              }}
            >
              <span className="font-mono font-semibold shrink-0 text-primary">{c.part_number}</span>
              <span className="text-muted-foreground truncate">{c.description}</span>
              {c.status !== 'Active' && <span className="shrink-0 text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium">{c.status}</span>}
              <span className="shrink-0 ml-auto text-muted-foreground italic">{c.category}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export function ReservationDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: currentUser } = useCurrentUser();
  // Includes managers, unlike the `isAdmin` on BuildDetailPage and others.
  const canManage = isManagerOrAbove(currentUser?.role);
  const isSuperAdmin = isSuperAdminRole(currentUser?.role);

  // fifoReallocate is imported from @/lib/fifoReallocate

  const isNew = id === 'new';
  const { hasPrev, hasNext, goPrev, goNext, position: listPosition } = useListNavigation(
    ['reservations', 'production-reservations'],
    '/reservations',
  );

  // ── Create-form state ──────────────────────────────────────────────────────
  type NewCompRow = { sku: string; type: string; qty: number; cost: number; desc: string };
  const [newForm, setNewForm] = useState({
    build_sku: '',
    build_id: null as string | null,
    quantity: 1,
    notes: '',
  });
  const [newComponents, setNewComponents] = useState<NewCompRow[]>([]);
  const [newCompLoading, setNewCompLoading] = useState(false);

  type StockShortage = { sku: string; type: string; available: number; onOrder: number; required: number };
  const [shortages, setShortages] = useState<StockShortage[]>([]);
  const [showStockWarning, setShowStockWarning] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLinkedAOs, setCancelLinkedAOs] = useState<{ id: string; order_number: string; status: string }[]>([]);
  const [newRowSku2, setNewRowSku2] = useState('');
  const [newRowType2, setNewRowType2] = useState('');
  const [newRowDesc2, setNewRowDesc2] = useState('');
  const [newRowQty2, setNewRowQty2] = useState(1);
  const [newRowCost2, setNewRowCost2] = useState(0);


  const sortComponents = (rows: NewCompRow[]): NewCompRow[] => {
    return [...rows].sort((a, b) => {
      const rankA = getComponentSortRank(a.type);
      const rankB = getComponentSortRank(b.type);
      if (rankA !== rankB) return rankA - rankB;
      return a.type.localeCompare(b.type);
    });
  };

  const loadBuildComponents = async (sku: string, buildId: string | null) => {
    if (!sku) { setNewComponents([]); return; }
    setNewCompLoading(true);
    try {
      // 1. Try build_components table (has qty_per_build)
      if (buildId) {
        const { data: bcRows } = await supabase
          .from('build_components')
          .select('component_sku, component_type, qty_per_build, cost_ex_vat')
          .eq('build_id', buildId);
        if (bcRows && bcRows.length > 0) {
          // Fetch descriptions from components
          const skus = bcRows.map((r: any) => r.component_sku).filter(Boolean);
          const descMap: Record<string, any> = {};
          if (skus.length > 0) {
            const { data: compData } = await supabase
              .from('components')
              .select('part_number, description, current_price')
              .in('part_number', skus);
            (compData || []).forEach((c: any) => { descMap[c.part_number] = c; });
          }
          setNewComponents(sortComponents(bcRows.map((r: any) => ({
            sku: r.component_sku,
            type: r.component_type || '',
            qty: r.qty_per_build || 1,
            cost: r.cost_ex_vat || descMap[r.component_sku]?.current_price || 0,
            desc: descMap[r.component_sku]?.description || '',
          }))));
          return;
        }
      }

      // 2. Fall back: components.build_sku is comma-separated, e.g. "AOI-1A-G3-R1,AOI-2B"
      const { data: allComps } = await supabase
        .from('components')
        .select('part_number, description, category, current_price, build_sku')
        .ilike('build_sku', `%${sku}%`)
        .order('category');

      const matched = (allComps || []).filter((c: any) => {
        if (!c.build_sku) return false;
        return c.build_sku
          .split(',')
          .map((s: string) => s.trim().replace(/-EOL$/i, ''))
          .includes(sku);
      });

      setNewComponents(sortComponents(matched.map((c: any) => ({
        sku: c.part_number,
        type: c.category || '',
        qty: 1,
        cost: c.current_price || 0,
        desc: c.description || '',
      }))));
    } catch (err) {
      console.error('[loadBuildComponents] Error:', err);
    } finally {
      setNewCompLoading(false);
    }
  };

  const executeCreate = async () => {
    // Merge committed rows with any uncommitted draft row.
    // This ensures the user never loses work even if they forget to press "+".
    const resolvedComponents: NewCompRow[] = [
      ...newComponents,
      ...(newRowSku2.trim()
        ? [{ sku: newRowSku2.trim(), type: newRowType2.trim(), desc: newRowDesc2.trim(), qty: newRowQty2, cost: newRowCost2 }]
        : []),
    ];

    // Generate next R-#### code — scan all R-* codes and find the true numeric max
    const { data: existingCodes } = await supabase
      .from('reservations')
      .select('reservation_code')
      .like('reservation_code', 'R-%');
    const maxNum = (existingCodes ?? []).reduce((max, row) => {
      const seg = row.reservation_code?.split('-')[1];
      const n = seg ? parseInt(seg, 10) : NaN;
      return !isNaN(n) && n > max ? n : max;
    }, 0);
    const reservation_code = `R-${String(maxNum + 1).padStart(4, '0')}`;

    const { data, error } = await supabase
      .from('reservations')
      .insert({
        reservation_code,
        build_sku: newForm.build_sku.trim(),
        build_id: newForm.build_id || null,
        quantity: newForm.quantity,
        notes: newForm.notes || null,
        status: 'Pending',
      })
      .select().single();
    if (error) throw error;

    // Use the user's explicitly chosen component list (including any uncommitted draft row).
    // No fallback — if the user cleared all components, create with none.
    const componentsToInsert = resolvedComponents.map(c => ({ sku: c.sku, type: c.type, qty: c.qty, cost: c.cost }));

    if (componentsToInsert.length > 0) {
      // Read stock first to compute per-reservation allocation
      const skus = componentsToInsert.map(c => c.sku);
      const { data: stockRows } = await supabase
        .from('components').select('part_number, available_qty, reserved_qty, expected_qty').in('part_number', skus);
      const stockMap: Record<string, any> = {};
      (stockRows || []).forEach((r: any) => { stockMap[r.part_number] = r; });

      // Compute per-component allocation (FIFO: this reservation gets current available)
      const allocations = componentsToInsert.map(comp => {
        const cur = stockMap[comp.sku];
        const totalQty = comp.qty * newForm.quantity;
        const avail = cur ? (cur.available_qty ?? 0) : 0;
        const fromStock = Math.min(totalQty, avail);
        const fromOrder = totalQty - fromStock;
        return { ...comp, fromStock, fromOrder, totalQty };
      });

      // Insert reservation_components with allocation data
      const insertPayload = allocations.map(c => ({
        reservation_id: data.id,
        component_sku:  c.sku,
        component_type: c.type,
        quantity:       c.qty,
        unit_cost:      c.cost,
        allocated_qty:  c.fromStock,
        expected_qty:   c.fromOrder,
      }));
      const { error: compError } = await supabase.from('reservation_components').insert(insertPayload);
      if (compError) throw compError;

      // Update component stock
      await Promise.all(allocations.map(c => {
        const cur = stockMap[c.sku];
        if (!cur) return Promise.resolve();
        return supabase.from('components').update({
          available_qty: (cur.available_qty ?? 0) - c.fromStock,
          reserved_qty: (cur.reserved_qty ?? 0) + c.fromStock,
          expected_qty: (cur.expected_qty ?? 0) + c.fromOrder,
        }).eq('part_number', c.sku);
      }));
    }

    // ── Sync alternative components back to build blueprint ──────────────
    // If a reservation component replaces one in the same category, add it to
    // the build BOM and swap active_config_skus so the blueprint stays current.
    if (newForm.build_id && componentsToInsert.length > 0) {
      try {
        // Fetch current build BOM + active config
        const [{ data: bomRows }, { data: buildRow }] = await Promise.all([
          supabase.from('build_components')
            .select('component_sku, component_type, qty_per_build, cost_ex_vat')
            .eq('build_id', newForm.build_id),
          supabase.from('builds')
            .select('active_config_skus')
            .eq('id', newForm.build_id)
            .single(),
        ]);

        const bomSkus = new Set((bomRows || []).map((r: any) => r.component_sku));
        const bomByCategory: Record<string, string[]> = {};
        (bomRows || []).forEach((r: any) => {
          const cat = (r.component_type || '').toLowerCase();
          if (!bomByCategory[cat]) bomByCategory[cat] = [];
          bomByCategory[cat].push(r.component_sku);
        });

        const activeSet = new Set(
          (buildRow?.active_config_skus || '')
            .split(',')
            .map((s: string) => s.trim())
            .filter(Boolean)
        );

        const toInsertIntoBom: any[] = [];
        const toActivate: string[] = [];
        const toDeactivate: string[] = [];

        for (const comp of componentsToInsert) {
          if (bomSkus.has(comp.sku)) continue; // already in BOM

          const cat = (comp.type || '').toLowerCase();
          const sameCatSkus = bomByCategory[cat] || [];

          // Add the new component to the build BOM
          toInsertIntoBom.push({
            build_id: newForm.build_id,
            component_sku: comp.sku,
            component_type: comp.type,
            qty_per_build: comp.qty,
            cost_ex_vat: comp.cost,
          });

          // Activate the new component
          toActivate.push(comp.sku);

          // Deactivate the counterpart(s) in the same category that were active
          for (const existingSku of sameCatSkus) {
            if (activeSet.has(existingSku)) {
              toDeactivate.push(existingSku);
            }
          }
        }

        if (toInsertIntoBom.length > 0) {
          await supabase.from('build_components').insert(toInsertIntoBom);

          // Update active_config_skus
          for (const sku of toDeactivate) activeSet.delete(sku);
          for (const sku of toActivate) activeSet.add(sku);
          const newActiveCsv = [...activeSet].join(',');
          await supabase.from('builds')
            .update({ active_config_skus: newActiveCsv })
            .eq('id', newForm.build_id);

          writeAuditLog({
            action: 'build_bom_auto_updated',
            entity_type: 'build',
            entity_id: newForm.build_id,
            entity_label: newForm.build_sku.trim(),
            new_value: {
              added: toInsertIntoBom.map(r => r.component_sku),
              activated: toActivate,
              deactivated: toDeactivate,
            },
          });
        }
      } catch (e) {
        // Non-critical — don't fail the reservation if blueprint sync fails
        console.warn('[Reservation] Failed to sync alternatives to build blueprint:', e);
      }
    }

    // Audit: reservation created
    writeAuditLog({
      action: 'reservation_created',
      entity_type: 'reservation',
      entity_id: data.id,
      entity_label: reservation_code,
      new_value: {
        reservation_code,
        build_sku: newForm.build_sku.trim(),
        quantity: newForm.quantity,
        notes: newForm.notes || null,
        components_count: componentsToInsert.length,
      },
    });

    return data;
  };

  const createMutation = useMutation({
    mutationFn: async ({ force = false }: { force?: boolean } = {}) => {
      if (!newForm.build_sku.trim()) throw new Error('Build SKU is required');
      if (newForm.quantity < 1) throw new Error('Quantity must be at least 1');

      // Warn if no stock AND nothing on order to cover the requirement.
      // Include any uncommitted draft row in this check.
      const allComponentsForCheck: NewCompRow[] = [
        ...newComponents,
        ...(newRowSku2.trim()
          ? [{ sku: newRowSku2.trim(), type: newRowType2.trim(), desc: newRowDesc2.trim(), qty: newRowQty2, cost: newRowCost2 }]
          : []),
      ];
      if (!force && allComponentsForCheck.length > 0) {
        const skus = allComponentsForCheck.map(c => c.sku);
        const { data: stockRows } = await supabase
          .from('components')
          .select('part_number, available_qty, on_order_qty, expected_qty')
          .in('part_number', skus);
        const stockMap: Record<string, any> = {};
        (stockRows || []).forEach((r: any) => { stockMap[r.part_number] = r; });

        const found: StockShortage[] = allComponentsForCheck
          .map(c => {
            const cur = stockMap[c.sku] ?? {};
            const avail = cur.available_qty ?? 0;
            // Free incoming = total on PO minus on-order already allocated to other reservations
            const onOrder = Math.max(0, (cur.on_order_qty ?? 0) - (cur.expected_qty ?? 0));
            const required = c.qty * newForm.quantity;
            return { sku: c.sku, type: c.type, available: avail, onOrder, required };
          })
          .filter(s => s.available + s.onOrder < s.required);

        if (found.length > 0) {
          setShortages(found);
          setShowStockWarning(true);
          throw new Error('__stock_warn__');
        }
      }

      return executeCreate();
    },
    onSuccess: (data) => {
      toast.success('Reservation created.');
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['admin-dashboard-summary'] });
      // replace, not push: the create form must not stay in history behind the
      // reservation it created. /reservations/new and /reservations/:id are the
      // same route, so going back to it does not remount this component - the
      // form comes back still holding everything that was just saved, which
      // reads as the new reservation and saves as a second copy of it.
      navigate(`/reservations/${data.id}`, { replace: true });
    },
    onError: (err: any) => {
      if (err.message !== '__stock_warn__') toast.error(`Failed: ${err.message}`);
    },
  });

  // ── Real-time stock colour coding for create form ─────────────────────────
  const compSkusForStock = [...new Set([...newComponents.map(c => c.sku), newRowSku2].filter(Boolean))];
  const { data: compStockMap, isFetching: stockFetching } = useQuery({
    queryKey: ['comp-stock-live', compSkusForStock.join(',')],
    queryFn: async () => {
      if (compSkusForStock.length === 0) return {} as Record<string, { available: number; reserved: number; onOrder: number }>;
      const { data } = await supabase
        .from('components')
        .select('part_number, available_qty, reserved_qty, on_order_qty, expected_qty')
        .in('part_number', compSkusForStock);
      const map: Record<string, { available: number; reserved: number; onOrder: number }> = {};
      (data || []).forEach((r: any) => {
        // Free incoming = total on PO minus on-order already allocated to other reservations
        const freeOnOrder = Math.max(0, (r.on_order_qty ?? 0) - (r.expected_qty ?? 0));
        map[r.part_number] = { available: r.available_qty ?? 0, reserved: r.reserved_qty ?? 0, onOrder: freeOnOrder };
      });
      return map;
    },
    enabled: isNew && compSkusForStock.length > 0,
    staleTime: 0, // Always fetch fresh — stock levels can change at any time
  });

  // The two states below are about the query, not about coverage, so they stay
  // here; classifyStockCoverage answers only the stock question.
  const getRowStockClass = (sku: string, rowQty: number): string => {
    if (!compStockMap || stockFetching) return 'bg-muted/30'; // query loading — neutral
    if (!sku || !compStockMap[sku]) return 'bg-background';   // no SKU or not found in DB
    const { available, onOrder } = compStockMap[sku];
    return STOCK_COVERAGE_CLASS[
      classifyStockCoverage(available, onOrder, rowQty * newForm.quantity)
    ];
  };

  // ── Header-edit state ──────────────────────────────────────────────────────
  const [isHeaderEditing, setIsHeaderEditing] = useState(false);
  const [headerForm, setHeaderForm] = useState({
    build_sku: '',
    build_id: null as string | null,
    quantity: 1,
    notes: '',
  });

  const saveHeader = useMutation({
    mutationFn: async () => {
      const oldUnits = reservation?.quantity || 1;
      const newUnits = headerForm.quantity;

      // Adjust stock when unit quantity changes — use per-reservation allocation
      if (newUnits !== oldUnits) {
        const { data: rcRows } = await supabase
          .from('reservation_components')
          .select('id, component_sku, quantity, allocated_qty, expected_qty')
          .eq('reservation_id', id);
        if (rcRows?.length) {
          const skus = rcRows.map(rc => rc.component_sku).filter(Boolean);
          const { data: stockRows } = await supabase
            .from('components').select('part_number, available_qty, reserved_qty, expected_qty').in('part_number', skus);
          const stockMap: Record<string, any> = {};
          (stockRows || []).forEach((r: any) => { stockMap[r.part_number] = r; });

          for (const rc of rcRows) {
            if (!rc.component_sku) continue;
            const cur = stockMap[rc.component_sku];
            if (!cur) continue;
            const perBuild = rc.quantity || 1;
            const delta = perBuild * (newUnits - oldUnits);
            if (delta > 0) {
              const avail = cur.available_qty ?? 0;
              const fromStock = Math.min(delta, avail);
              const fromOrder = delta - fromStock;
              await supabase.from('components').update({
                available_qty: avail - fromStock,
                reserved_qty: (cur.reserved_qty ?? 0) + fromStock,
                expected_qty: (cur.expected_qty ?? 0) + fromOrder,
              }).eq('part_number', rc.component_sku);
              await supabase.from('reservation_components').update({
                allocated_qty: (rc.allocated_qty ?? 0) + fromStock,
                expected_qty: (rc.expected_qty ?? 0) + fromOrder,
              }).eq('id', rc.id);
              // Update local cur for subsequent reads
              cur.available_qty = avail - fromStock;
              cur.reserved_qty = (cur.reserved_qty ?? 0) + fromStock;
              cur.expected_qty = (cur.expected_qty ?? 0) + fromOrder;
            } else {
              const release = Math.abs(delta);
              const rcExp = rc.expected_qty ?? 0;
              const rcAlloc = rc.allocated_qty ?? 0;
              const fromExpected = Math.min(release, rcExp);
              const fromAllocated = Math.min(release - fromExpected, rcAlloc);
              await supabase.from('components').update({
                available_qty: (cur.available_qty ?? 0) + fromAllocated,
                reserved_qty: Math.max(0, (cur.reserved_qty ?? 0) - fromAllocated),
                expected_qty: Math.max(0, (cur.expected_qty ?? 0) - fromExpected),
              }).eq('part_number', rc.component_sku);
              await supabase.from('reservation_components').update({
                allocated_qty: Math.max(0, rcAlloc - fromAllocated),
                expected_qty: Math.max(0, rcExp - fromExpected),
              }).eq('id', rc.id);
            }
          }
        }

        // FIFO reallocate if stock was released (qty decreased)
        if (newUnits < oldUnits) {
          const affectedSkus = (rcRows || []).map((rc: any) => rc.component_sku).filter(Boolean);
          await fifoReallocate(affectedSkus);
        }
      }

      const { data, error } = await supabase
        .from('reservations')
        .update({
          build_sku: headerForm.build_sku,
          build_id: headerForm.build_id || null,
          quantity: headerForm.quantity,
          notes: headerForm.notes || null,
        })
        .eq('id', id!)
        .select()
        .single();
      if (error) throw error;

      // Audit: reservation fields updated
      const oldValues: Record<string, unknown> = {};
      const newValues: Record<string, unknown> = {};
      if (reservation?.build_sku !== headerForm.build_sku) {
        oldValues.build_sku = reservation?.build_sku; newValues.build_sku = headerForm.build_sku;
      }
      if (reservation?.quantity !== headerForm.quantity) {
        oldValues.quantity = reservation?.quantity; newValues.quantity = headerForm.quantity;
      }
      if ((reservation?.notes || '') !== (headerForm.notes || '')) {
        oldValues.notes = reservation?.notes || null; newValues.notes = headerForm.notes || null;
      }
      if (Object.keys(newValues).length > 0) {
        writeAuditLog({
          action: 'reservation_fields_updated',
          entity_type: 'reservation',
          entity_id: id!,
          entity_label: reservation?.reservation_code,
          old_value: oldValues,
          new_value: newValues,
        });
      }

      return data;
    },
    onSuccess: () => {
      setIsHeaderEditing(false);
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      toast.success('Reservation updated.');
    },
    onError: (err: any) => toast.error(`Save failed: ${err.message}`),
  });

  const startHeaderEdit = () => {
    if (!reservation) return;
    setHeaderForm({
      build_sku: reservation.build_sku || reservation.builds?.sku || '',
      build_id: reservation.build_id || null,
      quantity: reservation.quantity ?? 1,
      notes: reservation.notes || '',
    });
    setIsHeaderEditing(true);
  };

  const [componentsExpanded, setComponentsExpanded] = useState(true);
  const [copiedSku, setCopiedSku] = useState<string | null>(null);
  const [copiedAll, setCopiedAll] = useState(false);

  // Component editing state
  type EditRow = { id?: string; sku: string; type: string; qty: number; cost: number };
  const [editMode, setEditMode] = useState(false);
  const [editRows, setEditRows] = useState<EditRow[]>([]);
  const [newRowSku, setNewRowSku] = useState('');
  const [newRowType, setNewRowType] = useState('');
  const [newRowQty, setNewRowQty] = useState(1);
  const [newRowCost, setNewRowCost] = useState(0);
  const [showAOWarning, setShowAOWarning] = useState(false);
  const [affectedAOs, setAffectedAOs] = useState<any[]>([]);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch reservation with full details
  const { data: reservation, isLoading, error: _error } = useQuery({
    queryKey: ['reservation', id],
    queryFn: async () => {
      if (!id) throw new Error('No reservation ID');

      // Get reservation
      const { data: res, error: resError } = await supabase
        .from('reservations')
        .select('*')
        .eq('id', id)
        .single();

      if (resError) throw resError;

      // Fetch build separately (no build_families table in new schema)
      let buildData: any = null;
      if (res.build_id) {
        const { data: bd } = await supabase
          .from('builds').select('id, sku, description').eq('id', res.build_id).single();
        buildData = bd;
      }

      // Fetch technician separately
      let techData: any = null;
      if (res.technician_id) {
        const { data: td } = await supabase
          .from('technicians').select('name, initials').eq('id', res.technician_id).single();
        techData = td;
      }

      const resWithRelations = { ...res, builds: buildData, technicians: techData };

      let parsedComponents: any[] = [];

      // Query reservation_components table
      const { data: resComponents } = await supabase
        .from('reservation_components')
        .select('id, component_sku, component_type, quantity, unit_cost, allocated_qty, expected_qty')
        .eq('reservation_id', res.id);

      if (resComponents && resComponents.length > 0) {
        // Fetch descriptions from components table
        const skus = resComponents.map(c => c.component_sku).filter(Boolean);
        const descMap: Record<string, string> = {};
        const compStockMap: Record<string, { on_order_qty: number; expected_qty: number }> = {};
        if (skus.length > 0) {
          const { data: compDetails } = await supabase
            .from('components')
            .select('part_number, description, on_order_qty, expected_qty')
            .in('part_number', skus);
          (compDetails || []).forEach((c: any) => {
            descMap[c.part_number] = c.description;
            compStockMap[c.part_number] = { on_order_qty: c.on_order_qty ?? 0, expected_qty: c.expected_qty ?? 0 };
          });
        }

        parsedComponents = resComponents.map(c => {
          const cs = compStockMap[c.component_sku];
          const compOnOrder = cs?.on_order_qty ?? 0;
          const compTotalExpected = cs?.expected_qty ?? (c.expected_qty ?? 0);
          const rcExpected = c.expected_qty ?? 0;
          const poAvailable = Math.max(0, compOnOrder - (compTotalExpected - rcExpected));
          const poCovered = Math.min(rcExpected, poAvailable);
          return {
            id: c.id,
            component_type: c.component_type || 'component',
            part_number: c.component_sku,
            description: descMap[c.component_sku] || c.component_sku,
            quantity: c.quantity || 1,
            cost: c.unit_cost || 0,
            allocated_qty: c.allocated_qty ?? 0,
            expected_qty: rcExpected,
            po_covered_qty: poCovered,           // portion backed by a real PO
            needs_ordering: rcExpected > poCovered, // expected demand with no PO
            source: 'reservation'
          };
        });
      }

      return {
        ...resWithRelations,
        components_list: parsedComponents
      } as ReservationWithDetails;
    },
    enabled: !!id && !isNew
  });

  // Fetch related build orders
  const { data: buildOrders, isLoading: ordersLoading } = useQuery({
    queryKey: ['reservation-build-orders', id],
    queryFn: async () => {
      if (!id) return [];

      const { data: ordersRaw, error } = await supabase
        .from('assembly_orders')
        .select('*')
        .eq('reservation_id', id)
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Fetch technicians for orders
      const orderTechIds = [...new Set((ordersRaw || []).map((o: any) => o.technician_id).filter(Boolean))];
      const orderTechsMap: Record<string, any> = {};
      if (orderTechIds.length > 0) {
        const { data: techsData } = await supabase
          .from('technicians').select('id, name, initials').in('id', orderTechIds);
        (techsData || []).forEach((t: any) => { orderTechsMap[t.id] = t; });
      }
      const data = (ordersRaw || []).map((o: any) => ({
        ...o, technicians: orderTechsMap[o.technician_id] || null
      }));
      return data;
    },
    enabled: !!id && !isNew
  });

  // Stock warnings now come from per-reservation allocation data in components_list

  // Update fulfillment mutation
  const updateFulfillment = useMutation({
    mutationFn: async (quantity_fulfilled: number) => {
      const oldFulfilled = reservation?.quantity_fulfilled ?? 0;

      const resQty = reservation?.quantity || 1;
      const newStatus = quantity_fulfilled >= resQty ? 'Completed'
        : quantity_fulfilled > 0 ? 'Partial'
        : 'Pending';
      const { data, error } = await supabase
        .from('reservations')
        .update({ quantity_fulfilled, status: newStatus })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Audit: fulfillment quantity changed
      writeAuditLog({
        action: 'reservation_fulfillment_updated',
        entity_type: 'reservation',
        entity_id: id!,
        entity_label: reservation?.reservation_code,
        old_value: { quantity_fulfilled: oldFulfilled },
        new_value: { quantity_fulfilled },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
    }
  });

  // Cancel reservation mutation
  const cancelReservation = useMutation({
    mutationFn: async ({ cancelAOs }: { cancelAOs: boolean }) => {
      // 1. Release stock using per-reservation allocation data.
      const { data: rcRows } = await supabase
        .from('reservation_components')
        .select('id, component_sku, allocated_qty, expected_qty')
        .eq('reservation_id', id);
      for (const rc of rcRows || []) {
        if (!rc.component_sku) continue;
        const allocToRelease = rc.allocated_qty ?? 0;
        const expToRelease = rc.expected_qty ?? 0;
        if (allocToRelease === 0 && expToRelease === 0) continue;
        const { data: cur } = await supabase
          .from('components')
          .select('available_qty, reserved_qty, expected_qty')
          .eq('part_number', rc.component_sku)
          .single();
        if (cur) {
          await supabase.from('components').update({
            available_qty: (cur.available_qty ?? 0) + allocToRelease,
            reserved_qty: Math.max(0, (cur.reserved_qty ?? 0) - allocToRelease),
            expected_qty: Math.max(0, (cur.expected_qty ?? 0) - expToRelease),
          }).eq('part_number', rc.component_sku);
        }
      }

      // 2. Optionally cancel linked assembly orders
      if (cancelAOs && cancelLinkedAOs.length > 0) {
        const aoIds = cancelLinkedAOs.map(ao => ao.id);
        await supabase
          .from('assembly_orders')
          .update({ status: 'cancelled' })
          .in('id', aoIds);
      }

      // 3. Mark reservation as Cancelled
      const { data, error } = await supabase
        .from('reservations')
        .update({
          status: 'Cancelled',
          notes: reservation?.notes
            ? `${reservation.notes}\n\nCancelled on ${new Date().toLocaleDateString('en-GB')}`
            : `Cancelled on ${new Date().toLocaleDateString('en-GB')}`,
        })
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // 3b. FIFO reallocate released stock (after reservation is cancelled)
      const releasedSkus = (rcRows || []).map((rc: any) => rc.component_sku).filter(Boolean);
      await fifoReallocate(releasedSkus);

      // Audit: reservation cancelled
      writeAuditLog({
        action: 'reservation_cancelled',
        entity_type: 'reservation',
        entity_id: id!,
        entity_label: reservation?.reservation_code,
        old_value: { status: reservation?.status },
        new_value: {
          status: 'Cancelled',
          cancelled_aos: cancelAOs ? cancelLinkedAOs.map(ao => ao.order_number) : [],
          components_released: (rcRows || []).map((c: any) => c.component_sku).filter(Boolean),
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['active-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['production-reservations'] });
      queryClient.invalidateQueries({ queryKey: ['production-orders'] });
      setCancelDialogOpen(false);
      navigate('/reservations');
    }
  });

  // Delete reservation mutation (super_admin only)
  const deleteReservation = useMutation({
    mutationFn: async () => {
      // 1. Release stock using per-reservation allocation data.
      const { data: rcRows } = await supabase
        .from('reservation_components')
        .select('id, component_sku, allocated_qty, expected_qty')
        .eq('reservation_id', id);
      for (const rc of rcRows || []) {
        if (!rc.component_sku) continue;
        const allocToRelease = rc.allocated_qty ?? 0;
        const expToRelease = rc.expected_qty ?? 0;
        if (allocToRelease === 0 && expToRelease === 0) continue;
        const { data: cur } = await supabase
          .from('components')
          .select('available_qty, reserved_qty, expected_qty')
          .eq('part_number', rc.component_sku)
          .single();
        if (cur) {
          await supabase.from('components').update({
            available_qty: (cur.available_qty ?? 0) + allocToRelease,
            reserved_qty: Math.max(0, (cur.reserved_qty ?? 0) - allocToRelease),
            expected_qty: Math.max(0, (cur.expected_qty ?? 0) - expToRelease),
          }).eq('part_number', rc.component_sku);
        }
      }

      // 2. Delete assembly_order_components for any linked orders
      const { data: linkedAOs } = await supabase
        .from('assembly_orders')
        .select('id')
        .eq('reservation_id', id);
      for (const ao of linkedAOs ?? []) {
        await supabase.from('assembly_order_components').delete().eq('ao_id', ao.id);
      }

      // 3. Delete posted_assembly_order_components + posted_assembly_orders linked to reservation
      const { data: linkedPAOs } = await supabase
        .from('posted_assembly_orders')
        .select('id')
        .eq('reservation_id', id);
      for (const pao of linkedPAOs ?? []) {
        await supabase.from('posted_assembly_order_components').delete().eq('posted_ao_id', pao.id);
      }
      await supabase.from('posted_assembly_orders').delete().eq('reservation_id', id);

      // 4. Delete linked assembly orders
      await supabase.from('assembly_orders').delete().eq('reservation_id', id);

      // 5. Delete reservation components
      await supabase.from('reservation_components').delete().eq('reservation_id', id);

      // 5b. FIFO reallocate released stock (after rc rows are deleted)
      const releasedSkus = (rcRows || []).map((rc: any) => rc.component_sku).filter(Boolean);
      await fifoReallocate(releasedSkus);

      // 6. Audit: reservation deleted (log before deleting so we have the entity_id)
      writeAuditLog({
        action: 'reservation_deleted',
        entity_type: 'reservation',
        entity_id: id!,
        entity_label: reservation?.reservation_code,
        old_value: {
          build_sku: reservation?.build_sku,
          quantity: reservation?.quantity,
          status: reservation?.status,
          components_count: (rcRows || []).length,
          linked_ao_count: (linkedAOs ?? []).length,
        },
      });

      // 7. Delete the reservation
      const { error } = await supabase.from('reservations').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['reservations'] });
      queryClient.invalidateQueries({ queryKey: ['active-reservations'] });
      toast.success('Reservation deleted');
      navigate('/reservations');
    },
    onError: (e: any) => toast.error(e.message || 'Failed to delete reservation'),
  });

  // ── Create new reservation ──────────────────────────────────────────────────
  if (isNew) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          <div>
            <h1 className="text-3xl font-bold">New Reservation</h1>
            <p className="text-muted-foreground">Create a new build reservation</p>
          </div>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Reservation Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1 col-span-2 md:col-span-1">
                <label className="text-sm font-medium">Build SKU <span className="text-red-500">*</span> <HelpTooltip tipKey="reservationDetail.buildSku" /></label>
                <BuildSearchInput
                  value={newForm.build_sku}
                  onChange={(sku, buildId) => {
                    setNewForm(f => ({ ...f, build_sku: sku, build_id: buildId }));
                    loadBuildComponents(sku, buildId);
                  }}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Units to Reserve <span className="text-red-500">*</span></label>
                <input
                  type="number" min="1"
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                  value={newForm.quantity}
                  onChange={e => setNewForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="space-y-1 col-span-2">
                <label className="text-sm font-medium">Notes</label>
                <textarea
                  className="w-full border rounded px-3 py-2 text-sm bg-background min-h-[60px]"
                  value={newForm.notes}
                  onChange={e => setNewForm(f => ({ ...f, notes: e.target.value }))}
                  placeholder="Optional notes…"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Components */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <Package className="h-5 w-5" />
                Components
                {newComponents.length > 0 && (
                  <span className="text-sm font-normal text-muted-foreground ml-1">({newComponents.length})</span>
                )}
              </CardTitle>
              {newCompLoading && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary" />
              )}
            </div>
          </CardHeader>
          <CardContent>
            {!newForm.build_sku && !newCompLoading && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Select a build SKU above to auto-load its components.
              </p>
            )}
            {newForm.build_sku && !newCompLoading && newComponents.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No components found for this build. Add them manually below.
              </p>
            )}
            {(newComponents.length > 0 || newForm.build_sku) && (
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 text-xs font-semibold w-24">Type</th>
                      <th className="text-left p-2 text-xs font-semibold w-36">Part Number</th>
                      <th className="text-left p-2 text-xs font-semibold">Description</th>
                      <th className="text-right p-2 text-xs font-semibold w-16">Available</th>
                      <th className="text-right p-2 text-xs font-semibold w-16">Reserved</th>
                      <th className="text-right p-2 text-xs font-semibold w-16">On Order</th>
                      <th className="text-right p-2 text-xs font-semibold w-14">Qty <HelpTooltip tipKey="reservationDetail.qtyPerBuild" /></th>
                      <th className="text-right p-2 text-xs font-semibold w-24">Unit Cost (€)</th>
                      <th className="p-2 w-8"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {newComponents.map((row, idx) => (
                      <tr key={idx} className={getRowStockClass(row.sku, row.qty)}>
                        <td className="p-1.5">
                          <input
                            className="w-full border rounded px-2 py-1 text-xs bg-background"
                            value={row.type}
                            placeholder="e.g. CPU"
                            onChange={e => setNewComponents(prev => prev.map((r, i) => i === idx ? { ...r, type: e.target.value } : r))}
                          />
                        </td>
                        <td className="p-1.5">
                          <ComponentSearchInput
                            value={row.sku}
                            onChange={(sku, type, cost, desc) => setNewComponents(prev => prev.map((r, i) =>
                              i === idx ? { ...r, sku, type: type || r.type, cost: cost || r.cost, desc: desc || r.desc } : r
                            ))}
                          />
                        </td>
                        <td className="p-1.5">
                          <input
                            className="w-full border rounded px-2 py-1 text-xs bg-background"
                            value={row.desc}
                            placeholder="Description"
                            onChange={e => setNewComponents(prev => prev.map((r, i) => i === idx ? { ...r, desc: e.target.value } : r))}
                          />
                        </td>
                        <td className="p-1.5 text-right text-xs text-muted-foreground tabular-nums">
                          {compStockMap?.[row.sku] != null ? compStockMap[row.sku].available : '—'}
                        </td>
                        <td className="p-1.5 text-right text-xs text-muted-foreground tabular-nums">
                          {compStockMap?.[row.sku] != null ? compStockMap[row.sku].reserved : '—'}
                        </td>
                        <td className="p-1.5 text-right text-xs text-muted-foreground tabular-nums">
                          {compStockMap?.[row.sku] != null ? compStockMap[row.sku].onOrder : '—'}
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number" min="1"
                            className="w-full border rounded px-2 py-1 text-xs text-right bg-background"
                            value={row.qty}
                            onChange={e => setNewComponents(prev => prev.map((r, i) => i === idx ? { ...r, qty: parseFloat(e.target.value) || 1 } : r))}
                          />
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number" min="0" step="0.01"
                            className="w-full border rounded px-2 py-1 text-xs text-right bg-background"
                            value={row.cost}
                            onChange={e => setNewComponents(prev => prev.map((r, i) => i === idx ? { ...r, cost: parseFloat(e.target.value) || 0 } : r))}
                          />
                        </td>
                        <td className="p-1.5 text-center">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setNewComponents(prev => prev.filter((_, i) => i !== idx))}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Add row */}
                    <tr className={newRowSku2 ? getRowStockClass(newRowSku2, newRowQty2) : 'bg-muted/30'}>
                      <td className="p-1.5">
                        <input
                          className="w-full border rounded px-2 py-1 text-xs bg-background"
                          placeholder="Type"
                          value={newRowType2}
                          onChange={e => setNewRowType2(e.target.value)}
                        />
                      </td>
                      <td className="p-1.5">
                        <ComponentSearchInput
                          value={newRowSku2}
                          onChange={(sku, type, cost, desc) => {
                            setNewRowSku2(sku);
                            if (type) setNewRowType2(type);
                            if (cost) setNewRowCost2(cost);
                            if (desc) setNewRowDesc2(desc);
                          }}
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          className="w-full border rounded px-2 py-1 text-xs bg-background"
                          placeholder="Description"
                          value={newRowDesc2}
                          onChange={e => setNewRowDesc2(e.target.value)}
                        />
                      </td>
                      <td className="p-1.5 text-right text-xs text-muted-foreground tabular-nums">
                        {newRowSku2 && compStockMap?.[newRowSku2] != null ? compStockMap[newRowSku2].available : '—'}
                      </td>
                      <td className="p-1.5 text-right text-xs text-muted-foreground tabular-nums">
                        {newRowSku2 && compStockMap?.[newRowSku2] != null ? compStockMap[newRowSku2].reserved : '—'}
                      </td>
                      <td className="p-1.5 text-right text-xs text-muted-foreground tabular-nums">
                        {newRowSku2 && compStockMap?.[newRowSku2] != null ? compStockMap[newRowSku2].onOrder : '—'}
                      </td>
                      <td className="p-1.5">
                        <input
                          type="number" min="1"
                          className="w-full border rounded px-2 py-1 text-xs text-right bg-background"
                          value={newRowQty2}
                          onChange={e => setNewRowQty2(parseFloat(e.target.value) || 1)}
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          type="number" min="0" step="0.01"
                          className="w-full border rounded px-2 py-1 text-xs text-right bg-background"
                          value={newRowCost2}
                          onChange={e => setNewRowCost2(parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="p-1.5 text-center">
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => {
                            if (!newRowSku2.trim()) return;
                            setNewComponents(prev => [...prev, { sku: newRowSku2.trim(), type: newRowType2.trim(), desc: newRowDesc2.trim(), qty: newRowQty2, cost: newRowCost2 }]);
                            setNewRowSku2(''); setNewRowType2(''); setNewRowDesc2(''); setNewRowQty2(1); setNewRowCost2(0);
                          }}>
                          <Plus className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            )}
            {newComponents.length > 0 && (
              <div className="flex flex-wrap gap-3 mt-3 text-xs text-muted-foreground">
                {/* Driven off the same maps as the row backgrounds, so the legend
                    cannot describe a colour the rows no longer use. */}
                {STOCK_COVERAGE_ORDER.map((state) => (
                  <span key={state} className="flex items-center gap-1.5">
                    <span className={`inline-block w-3 h-3 rounded-sm border ${STOCK_COVERAGE_CLASS[state]} ${STOCK_COVERAGE_SWATCH_BORDER[state]}`} />
                    {STOCK_COVERAGE_LABEL[state]}
                  </span>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => navigate('/reservations')}>Cancel</Button>
          <Button
            onClick={() => createMutation.mutate({})}
            disabled={createMutation.isPending || !newForm.build_sku.trim()}
          >
            {createMutation.isPending ? 'Creating…' : 'Create Reservation'}
          </Button>
        </div>

        {/* Stock warning dialog */}
        <Dialog open={showStockWarning} onOpenChange={open => { if (!open) setShowStockWarning(false); }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-yellow-500" />
                No Stock or Orders to Cover Demand
              </DialogTitle>
              <DialogDescription>
                The following components have no available stock and nothing on order to cover the{' '}
                {newForm.quantity} unit{newForm.quantity > 1 ? 's' : ''} being reserved.
              </DialogDescription>
            </DialogHeader>
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted">
                  <tr>
                    <th className="text-left p-2 text-xs font-semibold">Type</th>
                    <th className="text-left p-2 text-xs font-semibold">Part No.</th>
                    <th className="text-right p-2 text-xs font-semibold">Available</th>
                    <th className="text-right p-2 text-xs font-semibold">On Order</th>
                    <th className="text-right p-2 text-xs font-semibold">Required</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {shortages.map(s => (
                    <tr key={s.sku} className="bg-red-50">
                      <td className="p-2 text-xs text-muted-foreground">{s.type}</td>
                      <td className="p-2 text-xs font-mono">{s.sku}</td>
                      <td className="p-2 text-xs text-right text-red-600">{s.available}</td>
                      <td className="p-2 text-xs text-right text-blue-600">{s.onOrder}</td>
                      <td className="p-2 text-xs text-right font-semibold">{s.required}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowStockWarning(false)}>Go Back</Button>
              <Button
                onClick={() => { setShowStockWarning(false); createMutation.mutate({ force: true }); }}
                disabled={createMutation.isPending}
              >
                Reserve Anyway
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!reservation) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
        </div>
        <div className="text-center py-12">
          <AlertCircle className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
          <h3 className="text-lg font-semibold">Reservation not found</h3>
        </div>
      </div>
    );
  }

  // Status comes from @/lib/reservationStatus so this page and ReservationsPage
  // cannot drift apart again - they previously disagreed on both the fulfilled
  // count and whether Partial or In Progress wins.
  const { postedUnits, activeUnits } = sumAssemblyOrderUnits(buildOrders);
  const fulfilled = resolveFulfilledUnits(postedUnits, reservation.quantity_fulfilled);
  const derivedStatus = deriveReservationStatus(
    reservation.status,
    reservation.quantity,
    fulfilled,
    activeUnits
  );

  const fulfillmentPercentage = calculateFulfillmentPercentage(
    fulfilled,
    reservation.quantity
  );

  const isEditable = !isTerminalStatus(derivedStatus);
  const isCancellable = !isTerminalStatus(derivedStatus);

  const startEdit = () => {
    const rows = (reservation.components_list ?? []).map((c: any) => ({
      id: c.id,
      sku: c.part_number,
      type: c.component_type,
      qty: c.quantity,
      cost: c.cost,
    }));
    setEditRows(rows);
    setEditMode(true);
  };

  const doSave = async (activeAOs: any[]) => {
    setIsSaving(true);
    try {
      const oldComponents: any[] = reservation.components_list ?? [];

      // 1. Capture old allocations before delete
      const { data: oldRcRows } = await supabase
        .from('reservation_components')
        .select('component_sku, allocated_qty, expected_qty')
        .eq('reservation_id', id);
      const oldAllocMap: Record<string, { allocated: number; expected: number }> = {};
      (oldRcRows || []).forEach((r: any) => {
        oldAllocMap[r.component_sku] = { allocated: r.allocated_qty ?? 0, expected: r.expected_qty ?? 0 };
      });

      // 2. Replace reservation_components with new allocation data
      await supabase.from('reservation_components').delete().eq('reservation_id', id);
      if (editRows.length > 0) {
        // Read stock to compute allocations for new/changed components
        const newSkus = editRows.map(r => r.sku).filter(Boolean);
        const { data: stockRows } = await supabase
          .from('components').select('part_number, available_qty').in('part_number', newSkus);
        const availMap: Record<string, number> = {};
        (stockRows || []).forEach((r: any) => { availMap[r.part_number] = r.available_qty ?? 0; });

        // Carries existing allocations forward rather than re-deriving them,
        // and consumes free stock across the lines so two lines for the same
        // SKU cannot claim the same units. See @/lib/reservationAllocation.
        const allocations = recomputeAllocations(
          editRows.map(r => ({ sku: r.sku, qty: r.qty })),
          oldAllocMap,
          availMap,
          reservation?.quantity || 1
        );
        const insertRows = editRows.map((r, i) => ({
          reservation_id: id,
          component_sku: r.sku,
          component_type: r.type,
          quantity: r.qty,
          unit_cost: r.cost,
          allocated_qty: allocations[i].allocated,
          expected_qty: allocations[i].expected,
        }));
        const { error } = await supabase.from('reservation_components').insert(insertRows);
        if (error) throw error;
      }

      // 2. For pending AOs: update assembly_order_components to match
      // editRows store per-build qty — scale by each AO's unit count
      for (const ao of activeAOs.filter((o: any) => o.status === 'pending')) {
        const aoQty = ao.quantity || 1;
        await supabase.from('assembly_order_components').delete().eq('ao_id', ao.id);
        if (editRows.length > 0) {
          await supabase.from('assembly_order_components').insert(
            editRows.map(r => ({
              ao_id: ao.id,
              component_sku: r.sku,
              component_type: r.type,
              component_qty: r.qty * aoQty,
              cost_unit_ex_vat: r.cost,
              cost_batch_ex_vat: r.qty * aoQty * r.cost,
            }))
          );
        }
      }

      // 3. Bump bom_revision for all active AOs
      for (const ao of activeAOs) {
        await supabase
          .from('assembly_orders')
          .update({ bom_revision: (ao.bom_revision ?? 1) + 1 })
          .eq('id', ao.id);
      }

      // 4. Adjust reserved_qty on components table
      //    old reserved = old_per_build_qty * resUnits
      //    new reserved = new_per_build_qty * resUnits
      const resUnits = reservation?.quantity || 1;
      const deltaMap: Record<string, number> = {};
      for (const old of oldComponents) {
        if (!old.part_number) continue;
        deltaMap[old.part_number] = (deltaMap[old.part_number] ?? 0) - (old.quantity ?? 0) * resUnits;
      }
      for (const row of editRows) {
        if (!row.sku) continue;
        deltaMap[row.sku] = (deltaMap[row.sku] ?? 0) + row.qty * resUnits;
      }
      const skusToAdjust = Object.keys(deltaMap).filter(sku => deltaMap[sku] !== 0);
      if (skusToAdjust.length > 0) {
        const { data: stockRows } = await supabase
          .from('components').select('part_number, available_qty, reserved_qty, expected_qty').in('part_number', skusToAdjust);
        const stockMap: Record<string, any> = {};
        (stockRows || []).forEach((r: any) => { stockMap[r.part_number] = r; });
        await Promise.all(skusToAdjust.map(sku => {
          const cur = stockMap[sku];
          if (!cur) return Promise.resolve();
          const delta = deltaMap[sku];
          if (delta > 0) {
            const avail = cur.available_qty ?? 0;
            const fromStock = Math.min(delta, avail);
            const fromOrder = delta - fromStock;
            return supabase.from('components').update({
              available_qty: avail - fromStock,
              reserved_qty: (cur.reserved_qty ?? 0) + fromStock,
              expected_qty: (cur.expected_qty ?? 0) + fromOrder,
            }).eq('part_number', sku);
          } else {
            const release = Math.abs(delta);
            const fromExpected = Math.min(release, cur.expected_qty ?? 0);
            const fromReserved = Math.min(release - fromExpected, cur.reserved_qty ?? 0);
            return supabase.from('components').update({
              available_qty: (cur.available_qty ?? 0) + fromReserved,
              reserved_qty: Math.max(0, (cur.reserved_qty ?? 0) - fromReserved),
              expected_qty: Math.max(0, (cur.expected_qty ?? 0) - fromExpected),
            }).eq('part_number', sku);
          }
        }));
      }

      // 5. Audit records
      const auditRecords: any[] = [];
      for (const old of oldComponents) {
        const newRow = editRows.find(r => r.sku === old.part_number);
        if (!newRow) {
          auditRecords.push({ reservation_id: id, changed_by: currentUser?.id, change_type: 'remove', component_sku: old.part_number, old_qty: old.quantity, old_cost: old.cost });
        } else if (newRow.qty !== old.quantity || newRow.cost !== old.cost) {
          auditRecords.push({ reservation_id: id, changed_by: currentUser?.id, change_type: 'modify', component_sku: old.part_number, old_qty: old.quantity, new_qty: newRow.qty, old_cost: old.cost, new_cost: newRow.cost });
        }
      }
      for (const row of editRows) {
        if (!oldComponents.find((c: any) => c.part_number === row.sku)) {
          auditRecords.push({ reservation_id: id, changed_by: currentUser?.id, change_type: 'add', component_sku: row.sku, new_qty: row.qty, new_cost: row.cost });
        }
      }
      if (auditRecords.length > 0) {
        await supabase.from('reservation_component_audit').insert(auditRecords);
      }

      // Audit log: component changes
      for (const old of oldComponents) {
        const newRow = editRows.find(r => r.sku === old.part_number);
        if (!newRow) {
          writeAuditLog({
            action: 'reservation_component_removed',
            entity_type: 'reservation',
            entity_id: id!,
            entity_label: reservation?.reservation_code,
            old_value: { component_sku: old.part_number, component_type: old.component_type, quantity: old.quantity, cost: old.cost },
          });
        } else if (newRow.qty !== old.quantity || newRow.cost !== old.cost) {
          writeAuditLog({
            action: 'reservation_component_modified',
            entity_type: 'reservation',
            entity_id: id!,
            entity_label: reservation?.reservation_code,
            old_value: { component_sku: old.part_number, quantity: old.quantity, cost: old.cost },
            new_value: { component_sku: newRow.sku, quantity: newRow.qty, cost: newRow.cost },
          });
        }
      }
      for (const row of editRows) {
        if (!oldComponents.find((c: any) => c.part_number === row.sku)) {
          writeAuditLog({
            action: 'reservation_component_added',
            entity_type: 'reservation',
            entity_id: id!,
            entity_label: reservation?.reservation_code,
            new_value: { component_sku: row.sku, component_type: row.type, quantity: row.qty, cost: row.cost },
          });
        }
      }

      setEditMode(false);
      setShowAOWarning(false);
      setAffectedAOs([]);
      queryClient.invalidateQueries({ queryKey: ['reservation', id] });
      toast.success('Components updated.');
    } catch (err: any) {
      toast.error(`Save failed: ${err.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveComponents = async () => {
    if (editRows.some(r => !r.sku.trim())) {
      toast.error('All rows must have a part number.');
      return;
    }
    const { data: allAOs } = await supabase
      .from('assembly_orders')
      .select('id, order_number, status, quantity, bom_revision, technician:technicians!technician_id(name)')
      .eq('reservation_id', id);
    const active = (allAOs || []).filter((o: any) => !['Posted', 'Cancelled'].includes(o.status));
    if (active.length > 0) {
      setAffectedAOs(active);
      setShowAOWarning(true);
      return;
    }
    await doSave([]);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>
          {listPosition && (
            <div className="flex items-center gap-1">
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={goPrev} disabled={!hasPrev} title="Previous reservation">
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-xs text-muted-foreground px-1 min-w-[3rem] text-center">{listPosition}</span>
              <Button variant="outline" size="icon" className="h-8 w-8" onClick={goNext} disabled={!hasNext} title="Next reservation">
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          <div>
            <h1 className="text-3xl font-bold">{reservation.reservation_code}</h1>
            <p className="text-muted-foreground">Reservation Details</p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Badge className={getReservationStatusColor(derivedStatus as any)}>
            {getReservationStatusLabel(derivedStatus as any)}
          </Badge>
          {canManage && isEditable && !isHeaderEditing && (
            <Button variant="outline" size="sm" onClick={startHeaderEdit}>
              <Edit2 className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {canManage && isCancellable && !isHeaderEditing && (
            <Button
              variant="outline"
              size="sm"
              onClick={async () => {
                const { data: activeAOs } = await supabase
                  .from('assembly_orders')
                  .select('id, order_number, status')
                  .eq('reservation_id', id)
                  .not('status', 'in', '("Posted","Completed","cancelled")');
                setCancelLinkedAOs(activeAOs ?? []);
                setCancelDialogOpen(true);
              }}
              disabled={cancelReservation.isPending}
            >
              <XCircle className="h-4 w-4 mr-2" />
              {cancelReservation.isPending ? 'Cancelling…' : 'Cancel'}
            </Button>
          )}
          {isSuperAdmin && !isHeaderEditing && (
            <Button
              variant="destructive"
              size="sm"
              onClick={() => {
                if (confirm(`Permanently delete ${reservation.reservation_code}? This will also delete any linked assembly orders and restore reserved quantities. This cannot be undone.`)) {
                  deleteReservation.mutate();
                }
              }}
              disabled={deleteReservation.isPending}
            >
              <Trash2 className="h-4 w-4 mr-2" />
              {deleteReservation.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          )}
        </div>
      </div>

      {/* ── Header Edit Form ───────────────────────────────────────────────────── */}
      {isHeaderEditing && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Edit2 className="h-4 w-4" />
              Edit Reservation
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <label className="text-sm font-medium">Build SKU</label>
                <BuildSearchInput
                  value={headerForm.build_sku}
                  onChange={(sku, buildId) => setHeaderForm(f => ({ ...f, build_sku: sku, build_id: buildId }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Quantity</label>
                <input
                  type="number" min="1"
                  className="w-full border rounded px-3 py-2 text-sm bg-background"
                  value={headerForm.quantity}
                  onChange={e => setHeaderForm(f => ({ ...f, quantity: parseInt(e.target.value) || 1 }))}
                />
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Status</label>
                <div className="w-full border rounded px-3 py-2 text-sm bg-muted/50 text-muted-foreground">
                  {derivedStatus}
                  <span className="text-xs ml-2">(auto)</span>
                </div>
              </div>
            </div>
            <div className="space-y-1">
              <label className="text-sm font-medium">Notes</label>
              <textarea
                className="w-full border rounded px-3 py-2 text-sm bg-background min-h-[80px]"
                value={headerForm.notes}
                onChange={e => setHeaderForm(f => ({ ...f, notes: e.target.value }))}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setIsHeaderEditing(false)} disabled={saveHeader.isPending}>
                Cancel
              </Button>
              <Button onClick={() => saveHeader.mutate()} disabled={saveHeader.isPending}>
                {saveHeader.isPending ? 'Saving…' : 'Save Changes'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid gap-6 md:grid-cols-2">
        {/* Main Info Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Build Information
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Build SKU</label>
              <p className="font-medium">
                {reservation.builds?.sku || reservation.build_sku || 'N/A'}
              </p>
            </div>
            
            <div>
              <label className="text-sm text-muted-foreground">Description</label>
              <p className="font-medium">
                {reservation.builds?.description || 'N/A'}
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Family</label>
              <p className="font-medium">
                {reservation.build_sku?.split('-').slice(0, 2).join('-') || 'N/A'}
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Priority</label>
              <p className="font-medium">{reservation.priority || 0}</p>
            </div>
          </CardContent>
        </Card>

        {/* Fulfillment Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5" />
              Fulfillment Status
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm text-muted-foreground">Progress</label>
                <span className="text-2xl font-bold">
                  {fulfillmentPercentage}%
                </span>
              </div>
              <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all"
                  style={{ width: `${fulfillmentPercentage}%` }}
                />
              </div>
            </div>

            <div className={`grid gap-4 text-center ${activeUnits > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <div>
                <p className="text-2xl font-bold">{reservation.quantity}</p>
                <p className="text-xs text-muted-foreground">Total</p>
              </div>
              <div>
                <p className="text-2xl font-bold text-green-600">{fulfilled}</p>
                <p className="text-xs text-muted-foreground">Completed</p>
              </div>
              {activeUnits > 0 && (
                <div>
                  <p className="text-2xl font-bold text-blue-600">{activeUnits}</p>
                  <p className="text-xs text-muted-foreground">In Progress</p>
                </div>
              )}
              <div>
                <p className="text-2xl font-bold text-orange-600">
                  {Math.max(0, (reservation.quantity || 0) - fulfilled - activeUnits)}
                </p>
                <p className="text-xs text-muted-foreground">Remaining</p>
              </div>
            </div>

            {isEditable && (
              <div>
                <label className="text-sm text-muted-foreground block mb-2">
                  Update Fulfilled Quantity
                </label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    min="0"
                    max={reservation.quantity}
                    defaultValue={fulfilled}
                    className="flex-1 px-3 py-2 border rounded-md"
                    id="fulfilled-qty"
                  />
                  <Button
                    onClick={() => {
                      const input = document.getElementById('fulfilled-qty') as HTMLInputElement;
                      const value = parseInt(input.value, 10);
                      if (!isNaN(value) && value >= 0 && value <= reservation.quantity) {
                        updateFulfillment.mutate(value);
                      }
                    }}
                  >
                    Update
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Technician & Dates Card */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <User className="h-5 w-5" />
              Assignment & Timeline
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <label className="text-sm text-muted-foreground">Assigned To</label>
              <p className="font-medium">
                {reservation.technicians?.name || 'Unassigned'}
              </p>
            </div>

            <div>
              <label className="text-sm text-muted-foreground">Created</label>
              <p className="font-medium">
                {new Date(reservation.created_at).toLocaleDateString('en-GB')}
              </p>
            </div>

            {reservation.confirmed_at && (
              <div>
                <label className="text-sm text-muted-foreground">Confirmed</label>
                <p className="font-medium">
                  {new Date(reservation.confirmed_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            )}

            {reservation.completed_at && (
              <div>
                <label className="text-sm text-muted-foreground">Completed</label>
                <p className="font-medium">
                  {new Date(reservation.completed_at).toLocaleDateString('en-GB')}
                </p>
              </div>
            )}

            {reservation.source && (
              <div>
                <label className="text-sm text-muted-foreground">Source</label>
                <p className="font-medium capitalize">{reservation.source}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notes Card */}
        <Card>
          <CardHeader>
            <CardTitle>Notes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm whitespace-pre-wrap">
              {reservation.notes || 'No notes'}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Reserved Components - Collapsible Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <CardTitle>Reserved Components</CardTitle>
              {!editMode && reservation.components_list && reservation.components_list.length > 0 && (
                <span className="text-sm text-muted-foreground">
                  {reservation.components_list.length} components, {reservation.components_list.reduce((sum, c) => sum + c.quantity, 0)} units total
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {canManage && isEditable && (
                editMode ? (
                  <>
                    <Button variant="outline" size="sm" onClick={() => setEditMode(false)} disabled={isSaving}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveComponents} disabled={isSaving}>
                      {isSaving ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </>
                ) : (
                  <>
                    <Button variant="outline" size="sm" onClick={startEdit}>
                      <Edit2 className="h-4 w-4 mr-1" /> Edit Components
                    </Button>
                    <HelpTooltip tipKey="reservationDetail.blueprintSync" />
                  </>
                )
              )}
              {!editMode && reservation.components_list && reservation.components_list.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const lines = [...reservation.components_list!]
                      .sort((a: any, b: any) => getComponentSortRank(a.component_type || '') - getComponentSortRank(b.component_type || ''))
                      .map((c: any) => c.part_number || '').filter(Boolean);
                    // Write as both text and HTML table to prevent Excel scientific notation
                    const text = lines.join('\n');
                    const html = '<table>' + lines.map(l => `<tr><td style="mso-number-format:'\\@'">${l}</td></tr>`).join('') + '</table>';
                    navigator.clipboard.write([
                      new ClipboardItem({
                        'text/plain': new Blob([text], { type: 'text/plain' }),
                        'text/html': new Blob([html], { type: 'text/html' }),
                      }),
                    ]).catch(() => navigator.clipboard.writeText(text));
                    setCopiedAll(true);
                    setTimeout(() => setCopiedAll(false), 2000);
                  }}
                >
                  {copiedAll ? <ClipboardCheck className="h-4 w-4 mr-1 text-green-600" /> : <Copy className="h-4 w-4 mr-1" />}
                  {copiedAll ? 'Copied!' : 'Copy All SKUs'}
                </Button>
              )}
              {!editMode && reservation.components_list && reservation.components_list.length > 0 && (
                <Button variant="ghost" size="sm" onClick={() => setComponentsExpanded(!componentsExpanded)}>
                  {componentsExpanded ? (
                    <><ChevronUp className="h-4 w-4 mr-1" />Collapse</>
                  ) : (
                    <><ChevronDown className="h-4 w-4 mr-1" />Expand</>
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {editMode ? (
            /* ── Edit mode ── */
            <div className="space-y-3">
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-muted">
                    <tr>
                      <th className="text-left p-2 font-semibold text-xs">Type</th>
                      <th className="text-left p-2 font-semibold text-xs">Part Number</th>
                      <th className="text-right p-2 font-semibold text-xs">Qty</th>
                      <th className="text-right p-2 font-semibold text-xs">Unit Cost (€)</th>
                      <th className="p-2"></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {editRows.map((row, idx) => (
                      <tr key={idx} className="bg-background">
                        <td className="p-1.5">
                          <input
                            className="w-full border rounded px-2 py-1 text-xs bg-background"
                            value={row.type}
                            placeholder="e.g. CPU"
                            onChange={e => setEditRows(prev => prev.map((r, i) => i === idx ? { ...r, type: e.target.value } : r))}
                          />
                        </td>
                        <td className="p-1.5">
                          <ComponentSearchInput
                            value={row.sku}
                            onChange={(sku, type, cost) => setEditRows(prev => prev.map((r, i) =>
                              i === idx ? { ...r, sku, type: type || r.type, cost: cost || r.cost } : r
                            ))}
                          />

                        </td>
                        <td className="p-1.5">
                          <input
                            type="number" min="1"
                            className="w-20 border rounded px-2 py-1 text-xs text-right bg-background ml-auto block"
                            value={row.qty}
                            onChange={e => setEditRows(prev => prev.map((r, i) => i === idx ? { ...r, qty: parseFloat(e.target.value) || 1 } : r))}
                          />
                        </td>
                        <td className="p-1.5">
                          <input
                            type="number" min="0" step="0.01"
                            className="w-24 border rounded px-2 py-1 text-xs text-right bg-background ml-auto block"
                            value={row.cost}
                            onChange={e => setEditRows(prev => prev.map((r, i) => i === idx ? { ...r, cost: parseFloat(e.target.value) || 0 } : r))}
                          />
                        </td>
                        <td className="p-1.5 text-center">
                          <Button
                            variant="ghost" size="sm" className="h-7 w-7 p-0"
                            onClick={() => setEditRows(prev => prev.filter((_, i) => i !== idx))}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                    {/* Add new row */}
                    <tr className="bg-muted/30">
                      <td className="p-1.5">
                        <input
                          className="w-full border rounded px-2 py-1 text-xs bg-background"
                          placeholder="Type"
                          value={newRowType}
                          onChange={e => setNewRowType(e.target.value)}
                        />
                      </td>
                      <td className="p-1.5">
                        <ComponentSearchInput
                          value={newRowSku}
                          onChange={(sku, type, cost) => {
                            setNewRowSku(sku);
                            if (type) setNewRowType(type);
                            if (cost) setNewRowCost(cost);
                          }}
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          type="number" min="1"
                          className="w-20 border rounded px-2 py-1 text-xs text-right bg-background ml-auto block"
                          value={newRowQty}
                          onChange={e => setNewRowQty(parseFloat(e.target.value) || 1)}
                        />
                      </td>
                      <td className="p-1.5">
                        <input
                          type="number" min="0" step="0.01"
                          className="w-24 border rounded px-2 py-1 text-xs text-right bg-background ml-auto block"
                          value={newRowCost}
                          onChange={e => setNewRowCost(parseFloat(e.target.value) || 0)}
                        />
                      </td>
                      <td className="p-1.5 text-center">
                        <Button
                          variant="ghost" size="sm" className="h-7 w-7 p-0"
                          onClick={() => {
                            if (!newRowSku.trim()) return;
                            setEditRows(prev => [...prev, { sku: newRowSku.trim(), type: newRowType.trim(), qty: newRowQty, cost: newRowCost }]);
                            setNewRowSku(''); setNewRowType(''); setNewRowQty(1); setNewRowCost(0);
                          }}
                        >
                          <Plus className="h-3.5 w-3.5 text-green-600" />
                        </Button>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-[10px] text-muted-foreground">Press Enter in the Part Number field or click + to add a row.</p>
            </div>
          ) : (
            /* ── View mode ── */
            reservation.components_list && reservation.components_list.length > 0 ? (
              <>
                {componentsExpanded && (
                  <>
                    <div className="mb-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-900">
                      <strong>Note:</strong> These are the specific components reserved for this order, which may differ from the standard build template. <HelpTooltip tipKey="reservationDetail.fifo" />
                    </div>
                    <div className="border rounded-lg overflow-hidden">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="text-left p-3 font-semibold">Type</th>
                            <th className="text-left p-3 font-semibold">Item Code</th>
                            <th className="text-left p-3 font-semibold">Description</th>
                            <th className="text-right p-3 font-semibold">Qty</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y">
                          {[...reservation.components_list].sort((a: any, b: any) => {
                            return getComponentSortRank(a.component_type || '') - getComponentSortRank(b.component_type || '');
                          }).map((component: any, index: number) => {
                            const needsOrdering = component.needs_ordering;
                            const isAwaitingStock = !needsOrdering && (component.po_covered_qty ?? 0) > 0;
                            return (
                            <tr key={index} className="hover:bg-muted/50 transition-colors">
                              <td className="p-3 font-medium text-muted-foreground">
                                <div className="flex items-center gap-1.5">
                                  {getComponentTypeLabel(component.component_type)}
                                  {needsOrdering && (
                                    <span title={`${component.expected_qty - (component.po_covered_qty ?? 0)} unit(s) need to be ordered — no purchase order raised`}>
                                      <AlertTriangle className="h-3.5 w-3.5 text-red-500" />
                                    </span>
                                  )}
                                  {isAwaitingStock && (
                                    <span title={`${component.po_covered_qty} unit(s) awaiting stock delivery`}>
                                      <AlertTriangle className="h-3.5 w-3.5 text-amber-500" />
                                    </span>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 font-mono text-xs">
                                <div className="flex items-center gap-1.5 group/sku">
                                  <span>{component.part_number || 'N/A'}</span>
                                  {component.part_number && (
                                    <button
                                      className="shrink-0 opacity-0 group-hover/sku:opacity-100 transition-opacity p-0.5 hover:text-primary"
                                      title="Copy SKU"
                                      onClick={() => {
                                        const sku = component.part_number;
                                        const html = `<span style="mso-number-format:'\\@'">${sku}</span>`;
                                        navigator.clipboard.write([
                                          new ClipboardItem({
                                            'text/plain': new Blob([sku], { type: 'text/plain' }),
                                            'text/html': new Blob([html], { type: 'text/html' }),
                                          }),
                                        ]).catch(() => navigator.clipboard.writeText(sku));
                                        setCopiedSku(component.part_number);
                                        setTimeout(() => setCopiedSku(null), 1500);
                                      }}
                                    >
                                      {copiedSku === component.part_number
                                        ? <ClipboardCheck className="h-3.5 w-3.5 text-green-600" />
                                        : <Copy className="h-3.5 w-3.5 text-muted-foreground" />
                                      }
                                    </button>
                                  )}
                                </div>
                              </td>
                              <td className="p-3 text-muted-foreground">{component.description || 'No description available'}</td>
                              <td className="p-3 text-right">
                                <Badge variant="outline">{component.quantity}</Badge>
                              </td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </>
                )}
                {!componentsExpanded && (
                  <div className="text-center py-4 text-sm text-muted-foreground">
                    Click "Expand" to view component details
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8">
                <Package className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
                <p className="text-sm text-muted-foreground mb-2">No component details recorded for this reservation</p>
                <p className="text-xs text-muted-foreground">
                  This reservation may use the standard build template from {reservation.builds?.sku}
                </p>
                {reservation.builds?.sku && (
                  <Button variant="outline" size="sm" className="mt-4" asChild>
                    <Link to={`/builds/${reservation.build_id}`}>View Build Template</Link>
                  </Button>
                )}
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* AO Warning Dialog */}
      <Dialog open={showAOWarning} onOpenChange={open => { if (!open) { setShowAOWarning(false); setAffectedAOs([]); } }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              Active Assembly Orders Affected
            </DialogTitle>
            <DialogDescription>
              This reservation has active assembly orders. Review the impact before saving.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {affectedAOs.map((ao: any) => {
              const isPending = ao.status === 'pending';
              return (
                <div key={ao.id} className={`p-3 rounded-lg border text-sm ${isPending ? 'bg-blue-50 border-blue-200' : 'bg-yellow-50 border-yellow-200'}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-semibold">{ao.order_number}</span>
                    <Badge className={isPending ? 'bg-gray-100 text-gray-700' : 'bg-orange-100 text-orange-700'}>
                      {ao.status}
                    </Badge>
                  </div>
                  {ao.technician?.name && <p className="text-xs text-muted-foreground mt-0.5">{ao.technician.name}</p>}
                  <p className={`text-xs mt-1 ${isPending ? 'text-blue-700' : 'text-yellow-700'}`}>
                    {isPending
                      ? 'Pending — will automatically inherit the component changes.'
                      : 'Already in progress — the technician\'s sheet will NOT change. Confirm with the technician first; the order will be posted with these components.'}
                  </p>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAOWarning(false); setAffectedAOs([]); }}>
              Cancel
            </Button>
            <Button onClick={() => doSave(affectedAOs)} disabled={isSaving}>
              {isSaving ? 'Saving…' : 'Proceed Anyway'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Related Build Orders */}
      <Card>
        <CardHeader>
          <CardTitle>Related Assembly Orders</CardTitle>
        </CardHeader>
        <CardContent>
          {ordersLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : buildOrders && buildOrders.length > 0 ? (
            <div className="space-y-3">
              {buildOrders.map((order: any) => {
                const statusColors: Record<string, string> = {
                  'pending':     'bg-amber-100 text-amber-800',
                  'In Progress': 'bg-purple-100 text-purple-800',
                  'building':    'bg-purple-100 text-purple-800',
                  'software':    'bg-indigo-100 text-indigo-800',
                  'testing':     'bg-cyan-100 text-cyan-800',
                  'awaiting_review': 'bg-orange-100 text-orange-800',
                  'Posted':      'bg-blue-100 text-blue-800',
                  'Completed':   'bg-green-100 text-green-800',
                  'cancelled':   'bg-red-100 text-red-800',
                  'Cancelled':   'bg-red-100 text-red-800',
                  'On Hold':     'bg-yellow-100 text-yellow-800',
                };

                return (
                  <Link
                    key={order.id}
                    to={`/assembly-orders/${order.id}`}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <p className="font-semibold">{order.order_number}</p>
                        <Badge className={statusColors[order.status] || 'bg-gray-200 text-gray-800'}>
                          {order.status.replace('_', ' ')}
                        </Badge>
                      </div>
                      
                      <div className="flex items-center gap-4 text-sm text-muted-foreground">
                        <span>Quantity: {order.quantity}</span>
                        {order.technicians && (
                          <span>• {order.technicians.name}</span>
                        )}
                        {order.to_number && (
                          <span>• TO: {order.to_number}</span>
                        )}
                      </div>

                      <div className="flex items-center gap-2 text-xs text-muted-foreground mt-1">
                        <Calendar className="h-3 w-3" />
                        <span>
                          Created: {new Date(order.created_at).toLocaleDateString('en-GB')}
                        </span>
                        {order.completed_at && (
                          <>
                            <span>•</span>
                            <span>
                              Completed: {new Date(order.completed_at).toLocaleDateString('en-GB')}
                            </span>
                          </>
                        )}
                      </div>
                    </div>

                    {(order.status === 'Completed' || order.status === 'Posted') && (
                      <CheckCircle2 className="h-5 w-5 text-green-600 ml-4" />
                    )}
                  </Link>
                );
              })}

              {/* Summary */}
              <div className="mt-4 pt-4 border-t">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Orders:</span>
                  <span className="font-semibold">{buildOrders.length}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Total Units in Orders:</span>
                  <span className="font-semibold">
                    {buildOrders.reduce((sum: number, o: any) => sum + (o.quantity || 0), 0)}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Completed Orders:</span>
                  <span className="font-semibold">
                    {buildOrders.filter((o: any) => o.status === 'Completed' || o.status === 'Posted').length}
                  </span>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-8">
              <Package className="h-8 w-8 mx-auto mb-2 text-muted-foreground opacity-50" />
              <p className="text-sm text-muted-foreground">
                No build orders created yet for this reservation
              </p>
              <Button variant="outline" size="sm" className="mt-4" asChild>
                <Link to={`/assembly-orders/new?reservation_id=${id}`}>
                  Create Assembly Order
                </Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Cancel reservation confirmation dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="h-5 w-5 text-destructive" />
              Cancel Reservation
            </DialogTitle>
            <DialogDescription>
              This will mark the reservation as Cancelled and return reserved components to stock.
            </DialogDescription>
          </DialogHeader>
          {cancelLinkedAOs.length > 0 && (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-3 space-y-1.5">
              <p className="text-sm font-medium text-destructive">
                {cancelLinkedAOs.length} active assembly order{cancelLinkedAOs.length > 1 ? 's are' : ' is'} linked to this reservation:
              </p>
              <ul className="text-sm space-y-0.5">
                {cancelLinkedAOs.map(ao => (
                  <li key={ao.id} className="font-mono text-xs">
                    {ao.order_number} <span className="text-muted-foreground capitalize">({ao.status})</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground pt-1">
                These orders will also be cancelled.
              </p>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelDialogOpen(false)} disabled={cancelReservation.isPending}>
              Keep Reservation
            </Button>
            <Button
              variant="destructive"
              disabled={cancelReservation.isPending}
              onClick={() => cancelReservation.mutate({ cancelAOs: cancelLinkedAOs.length > 0 })}
            >
              {cancelReservation.isPending ? 'Cancelling…' : cancelLinkedAOs.length > 0 ? 'Cancel Reservation & Orders' : 'Cancel Reservation'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
